import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import {
  CreditCardIcon,
  BellIcon,
  DocumentIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  CogIcon,
  UserGroupIcon,
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  CalendarDaysIcon,
  TrashIcon,
  CurrencyDollarIcon,
  DocumentDuplicateIcon,
  ShieldExclamationIcon,
  ArchiveBoxIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  WrenchScrewdriverIcon,
  BookOpenIcon,
  ChartBarIcon,
  CogIcon as CollectionConfigIcon,
  BanknotesIcon,
  RectangleStackIcon,
  ChartPieIcon
} from '@heroicons/react/24/outline';

const MainLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [developersOpen, setDevelopersOpen] = useState(false);
  const [collectionScoresOpen, setCollectionScoresOpen] = useState(false);
  const [negotiationOpen, setNegotiationOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const { canAccessModule } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  // Define navigation items with their required permissions
  const allNavigationItems = [
    { name: 'Facturas', href: '/payments', icon: CreditCardIcon, module: 'pagos' },
    { name: 'Histórico', href: '/historic', icon: ArchiveBoxIcon, module: 'pagos' },
    { name: 'Notificaciones', href: '/notifications', icon: BellIcon, module: 'notificaciones' },
    { name: 'Vectores', href: '/notification-vectors', icon: CalendarDaysIcon, module: 'notificaciones' },
    { name: 'Archivos', href: '/files', icon: DocumentIcon, module: 'lotes' },
    { name: 'Templates', href: '/templates', icon: DocumentTextIcon, module: 'templates' },
    { name: 'Biblioteca', href: '/template-library', icon: BookOpenIcon, module: 'templates' },
    { name: 'Publicadores', href: '/publisher-systems', icon: ShieldCheckIcon, module: 'publishers' },
    { name: 'Billers', href: '/billers', icon: BuildingOfficeIcon, module: 'billers' },
    { name: 'Canales', href: '/channels', icon: CogIcon, module: 'notificaciones' },
  ];

  // Filter navigation based on permissions and add admin items
  const navigation = useMemo(() => {
    const items = allNavigationItems.filter(item => canAccessModule(item.module));

    // Add admin-only items if user is admin
    if (isAdmin) {
      items.push(
        { name: 'Usuarios', href: '/users', icon: UserGroupIcon },
        { name: 'Roles', href: '/roles', icon: ShieldCheckIcon },
        { name: 'Precios', href: '/prices', icon: CurrencyDollarIcon },
        { name: 'Depuración', href: '/purge', icon: TrashIcon },
        { name: 'Admin', href: '/admin', icon: ShieldExclamationIcon },
        { name: 'Industrias', href: '/industries', icon: BuildingOfficeIcon }
      );
    }

    // v3.1.0: Add Publisher Users menu if user is Publisher Admin
    // Note: Publisher Admin role should have scope='publisher' in database
    if (user?.user_type === 'publisher' && user?.role_name?.includes('Publisher Admin')) {
      items.push(
        { name: 'Usuarios Publisher', href: '/publisher/users', icon: UserGroupIcon }
      );
    }

    // v3.1.0: Biller Users management moved to BillerDashboard -> Configuración tab
    // No menu item needed here as Biller users don't see the sidebar

    // Add monitoring module based on permissions
    if (canAccessModule('monitoring')) {
      items.push(
        { name: 'Monitoreo', href: '/monitoring', icon: ChartPieIcon, module: 'monitoring' }
      );
    }

    return items;
  }, [canAccessModule, isAdmin, user]);

  const developersNavigation = [
    { name: 'Documentación Sistema', href: '/docs', icon: DocumentTextIcon },
    { name: 'API Documentation', href: '/apidoc', icon: CodeBracketIcon },
    { name: 'Logs', href: '/logs', icon: DocumentDuplicateIcon },
  ];

  const collectionScoresNavigation = [
    { name: 'Dashboard Avanzado', href: '/collection-scores/advanced', icon: ChartBarIcon },
    { name: 'Configuración', href: '/collection-scores/config', icon: CollectionConfigIcon, adminOnly: true },
  ];

  const negotiationNavigation = [
    { name: 'Planes', href: '/negotiation/plans', icon: BanknotesIcon, adminOnly: true },
    { name: 'Campañas', href: '/negotiation/campaigns', icon: RectangleStackIcon, adminOnly: true },
  ];

  // Verificar si alguna ruta de developers está activa
  const isDevelopersActive = developersNavigation.some(item => location.pathname === item.href);

  // Verificar si alguna ruta de collection scores está activa
  const isCollectionScoresActive = collectionScoresNavigation.some(item => location.pathname === item.href);

  // Verificar si alguna ruta de negotiation está activa
  const isNegotiationActive = negotiationNavigation.some(item => location.pathname === item.href);

  // Auto-expandir las secciones si estamos en una de sus rutas
  useEffect(() => {
    if (isDevelopersActive) {
      setDevelopersOpen(true);
    }
    if (isCollectionScoresActive) {
      setCollectionScoresOpen(true);
    }
    if (isNegotiationActive) {
      setNegotiationOpen(true);
    }
  }, [isDevelopersActive, isCollectionScoresActive, isNegotiationActive]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Biller users don't see the main sidebar - they only access their biller dashboard
  const isBillerUser = user?.user_type === 'biller';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {!isBillerUser && sidebarOpen && (
        <div
          className="fixed inset-0 flex z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75"></div>
        </div>
      )}

      {/* Sidebar - Hidden for biller users */}
      {!isBillerUser && (
        <div className={`
          fixed inset-y-0 left-0 flex flex-col w-64 bg-white border-r border-gray-200 pt-5 pb-4 overflow-y-auto z-50
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 transition-transform duration-300 ease-in-out
        `}>
        {/* Logo and close button */}
        <div className="flex items-center justify-between flex-shrink-0 px-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">A</span>
            </div>
            <div className="ml-3">
              <h1 className="text-lg font-semibold text-gray-900">ACM</h1>
              <p className="text-xs text-gray-500">Aura Collections Manager</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden p-1 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-8 flex-1 px-4 space-y-1">
          {/* Main navigation items */}
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;
            
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`
                  group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200
                  ${isActive 
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <Icon 
                  className={`
                    mr-3 flex-shrink-0 h-5 w-5
                    ${isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}
                  `}
                />
                {item.name}
              </Link>
            );
          })}

          {/* Collection Scores section - Only show if user has score permissions */}
          {(canAccessModule('score', 'basico') || canAccessModule('score', 'avanzado') || canAccessModule('score', 'alertas')) && (
            <div className="pt-4">
              {/* Collection Scores header */}
              <button
                onClick={() => setCollectionScoresOpen(!collectionScoresOpen)}
                className={`
                  group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200
                  ${isCollectionScoresActive
                    ? 'bg-green-50 text-green-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <ChartBarIcon
                  className={`
                    mr-3 flex-shrink-0 h-5 w-5
                    ${isCollectionScoresActive ? 'text-green-500' : 'text-gray-400 group-hover:text-gray-500'}
                  `}
                />
                <span className="flex-1 text-left">Score Cobranza</span>
                {collectionScoresOpen ? (
                  <ChevronDownIcon className="ml-2 h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="ml-2 h-4 w-4" />
                )}
              </button>

              {/* Collection Scores sub-menu */}
              {collectionScoresOpen && (
                <div className="ml-8 mt-1 space-y-1">
                  {collectionScoresNavigation.map((item) => {
                    // Verificar si el elemento requiere admin y el usuario no es admin
                    if (item.adminOnly && !isAdmin) {
                      return null;
                    }

                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`
                          group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200
                          ${isActive
                            ? 'bg-green-50 text-green-700 border-r-2 border-green-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        <Icon
                          className={`
                            mr-3 flex-shrink-0 h-4 w-4
                            ${isActive ? 'text-green-500' : 'text-gray-400 group-hover:text-gray-500'}
                          `}
                        />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Negotiation section - only show for admin users */}
          {isAdmin && (
            <div className="pt-4">
              {/* Negotiation header */}
              <button
                onClick={() => setNegotiationOpen(!negotiationOpen)}
                className={`
                  group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200
                  ${isNegotiationActive
                    ? 'bg-orange-50 text-orange-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <BanknotesIcon
                  className={`
                    mr-3 flex-shrink-0 h-5 w-5
                    ${isNegotiationActive ? 'text-orange-500' : 'text-gray-400 group-hover:text-gray-500'}
                  `}
                />
                <span className="flex-1 text-left">Renegociación</span>
                {negotiationOpen ? (
                  <ChevronDownIcon className="ml-2 h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="ml-2 h-4 w-4" />
                )}
              </button>

              {/* Negotiation sub-menu */}
              {negotiationOpen && (
                <div className="ml-8 mt-1 space-y-1">
                  {negotiationNavigation.map((item) => {
                    // Verificar si el elemento requiere admin y el usuario no es admin
                    if (item.adminOnly && !isAdmin) {
                      return null;
                    }

                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`
                          group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200
                          ${isActive
                            ? 'bg-orange-50 text-orange-700 border-r-2 border-orange-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        <Icon
                          className={`
                            mr-3 flex-shrink-0 h-4 w-4
                            ${isActive ? 'text-orange-500' : 'text-gray-400 group-hover:text-gray-500'}
                          `}
                        />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Developers section - only show for admin users */}
          {isAdmin && (
            <div className="pt-4">
              {/* Developers header */}
              <button
                onClick={() => setDevelopersOpen(!developersOpen)}
                className={`
                  group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200
                  ${isDevelopersActive
                    ? 'bg-purple-50 text-purple-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <WrenchScrewdriverIcon
                  className={`
                    mr-3 flex-shrink-0 h-5 w-5
                    ${isDevelopersActive ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-500'}
                  `}
                />
                <span className="flex-1 text-left">Developers</span>
                {developersOpen ? (
                  <ChevronDownIcon className="ml-2 h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="ml-2 h-4 w-4" />
                )}
              </button>

              {/* Developers sub-menu */}
              {developersOpen && (
                <div className="ml-8 mt-1 space-y-1">
                  {developersNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`
                          group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200
                          ${isActive
                            ? 'bg-purple-50 text-purple-700 border-r-2 border-purple-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        <Icon
                          className={`
                            mr-3 flex-shrink-0 h-4 w-4
                            ${isActive ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-500'}
                          `}
                        />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>
        </div>
      )}

      {/* Main content */}
      <div className={`flex flex-col flex-1 ${!isBillerUser ? 'md:pl-64' : ''}`}>
        {/* Top navigation bar */}
        <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow-sm border-b border-gray-200">
          {!isBillerUser && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
          )}

          <div className="flex-1 px-4 flex justify-between items-center">
            <div className="flex-1">
              {/* Page title could go here */}
            </div>

            {/* Profile dropdown */}
            <div className="relative">
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="flex items-center space-x-2 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 p-2"
              >
                <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                  <UserIcon className="h-5 w-5 text-gray-600" />
                </div>
                <span className="hidden md:block text-gray-700 font-medium">
                  {user?.full_name?.split(' ')[0] || user?.email}
                </span>
              </button>

              {/* Dropdown menu */}
              {profileMenuOpen && (
                <div className="profile-dropdown origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <div className="py-1">
                    {/* User info section */}
                    <div className="px-4 py-3 border-b border-gray-200">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {user?.full_name || 'Usuario'}
                      </p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
                      {user?.role_name && (
                        <div className="mt-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            {user.role_name}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Menu items */}
                    <Link
                      to="/profile"
                      onClick={() => setProfileMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                    >
                      <UserIcon className="mr-2 h-4 w-4" />
                      Perfil
                    </Link>
                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                    >
                      <ArrowRightOnRectangleIcon className="mr-2 h-4 w-4" />
                      Cerrar Sesión
                    </button>

                    {/* Divider */}
                    <div className="border-t border-gray-200 my-1"></div>

                    {/* Version info section */}
                    <div className="px-4 py-2 bg-gray-50">
                      <p className="text-xs font-medium text-gray-500 mb-1">Versiones</p>
                      <div className="space-y-0.5">
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">Frontend:</span> v3.0.1
                        </p>
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">Backend:</span> v3.0.0
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {/* Close profile menu when clicking outside */}
      {profileMenuOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setProfileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default MainLayout;